import cn from 'classnames';
import type { ReactNode } from 'react';

export interface KeyValueItem {
  key: ReactNode;
  value: ReactNode;
  rowClassName?: string;
  suppressStripe?: boolean;
}

export type KeyValueStripeTableLayout = 'horizontal' | 'vertical';

export interface KeyValueStripeTableProps {
  items: KeyValueItem[];
  className?: string;
  activeRowIndex?: number;
  equalColumns?: boolean;
  valueAlign?: 'left' | 'right';
  valueNoWrap?: boolean;
  bordered?: boolean;
  firstWhite?: boolean;
  layout?: KeyValueStripeTableLayout;
}

type KeyValueStripeRowProps = {
  item: KeyValueItem;
  rowIndex: number;
  layout: KeyValueStripeTableLayout;
  activeRowIndex: number | undefined;
  firstWhite: boolean;
  equalColumns?: boolean;
  valueAlign: 'left' | 'right';
  valueNoWrap?: boolean;
};

const KeyValueStripeRow = ({
  item,
  rowIndex,
  layout,
  activeRowIndex,
  firstWhite,
  equalColumns,
  valueAlign,
  valueNoWrap
}: KeyValueStripeRowProps) => {
  const isActive = activeRowIndex !== undefined && rowIndex === activeRowIndex;
  const isLightRow = firstWhite ? rowIndex % 2 === 1 : rowIndex % 2 === 0;
  const rowClass = cn(
    !item.suppressStripe && isLightRow && 'bg-light',
    isActive && 'bg-secondary bg-opacity-10',
    item.rowClassName
  );

  if (layout === 'vertical') {
    return (
      <tr className={rowClass}>
        <td colSpan={2} className="py-3 px-3">
          <div className="fw-semibold fs-7 text-gray-500">{item.key}</div>
          <div
            className={cn(
              'fw-bold fs-6 text-gray-900',
              valueAlign === 'right' && 'text-end',
              valueNoWrap && 'text-nowrap'
            )}
          >
            {item.value}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={rowClass}>
      <td className={cn('fw-semibold fs-7 text-gray-500 py-2 px-3', equalColumns ? 'w-50' : 'w-150px')}>{item.key}</td>
      <td
        className={cn(
          'fw-semibold fs-7 text-gray-800 py-2 px-3',
          equalColumns && 'w-50',
          valueAlign === 'right' && 'text-end',
          valueNoWrap && 'text-nowrap'
        )}
      >
        {item.value}
      </td>
    </tr>
  );
};

export const KeyValueStripeTable = ({
  items,
  className,
  activeRowIndex,
  equalColumns,
  valueAlign = 'left',
  valueNoWrap,
  bordered = true,
  firstWhite = false,
  layout = 'horizontal'
}: KeyValueStripeTableProps) => (
  <table className={cn('table mb-0', bordered && 'table-row-bordered', className)}>
    <tbody>
      {items.map((item, rowIndex) => (
        <KeyValueStripeRow
          key={rowIndex}
          item={item}
          rowIndex={rowIndex}
          layout={layout}
          activeRowIndex={activeRowIndex}
          firstWhite={firstWhite}
          equalColumns={equalColumns}
          valueAlign={valueAlign}
          valueNoWrap={valueNoWrap}
        />
      ))}
    </tbody>
  </table>
);
